import React, { useEffect, useState, useRef } from "react";
import { Modal, Button, Form, Table, Spinner, Alert } from "react-bootstrap";
import api from "../../axiosConfig";

export default function EchipamentPickerModal({
  show,
  onHide,
  onPick,
  initialQuery = "",
  title = "Selectează Echipament",
}) {
  const [q, setQ] = useState(initialQuery);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(null);
  const [page, setPage] = useState(1);

  const loadingPageRef = useRef(null);
  const lastFetchedQueryRef = useRef(null);

  const limit = 20;

  const fetchPage = async (nextPage = 1, query = q) => {
    const qq = (query || "").trim();

    if (loadingPageRef.current === nextPage) {
      return;
    }
    loadingPageRef.current = nextPage;

    setBusy(true);
    setErr("");

    try {
      const res = await api.get("/contor/echipamente", {
        params: {
          skip: (nextPage - 1) * limit,
          limit: limit,
          search: qq || undefined,
        },
      });

      const list = res.data?.items || [];
      const t = res.data?.total ?? null;

      const mapped = list.map((it) => {
        const label = `${it.bxseria} • ${it.part_name || ""} (${it.utilizator_name || ""})`;
        return {
          id: it.bxid,
          label,
          raw: it,
          seria: it.bxseria,
          codech: it.bxcodech,
          descr: it.bxdescrechip,
          part_name: it.part_name,
          client_name: it.utilizator_name,
        };
      });

      setItems((prev) => (nextPage === 1 ? mapped : [...prev, ...mapped]));
      setTotal(t);
      setPage(nextPage);
      lastFetchedQueryRef.current = qq;
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Eroare la încărcarea echipamentelor.");
      if (nextPage === 1) {
        setItems([]);
        setTotal(null);
      }
    } finally {
      setBusy(false);
      loadingPageRef.current = null;
    }
  };

  useEffect(() => {
    if (!show) return;

    const initQ = initialQuery || "";
    setQ(initQ);
    setItems([]);
    setTotal(null);
    setPage(1);
    setErr("");
    lastFetchedQueryRef.current = null;

    fetchPage(1, initQ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Căutare automată la tastare (debounce)
  useEffect(() => {
    if (!show) return;

    const query = q.trim();
    if (lastFetchedQueryRef.current === query) {
      return;
    }

    const t = setTimeout(() => {
      fetchPage(1, q);
    }, 300);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, show]);

  const hasMore = total != null ? page * limit < total : items.length === limit;

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 15) {
      if (!busy && hasMore) {
        fetchPage(page + 1, q);
      }
    }
  };

  const pick = (opt) => {
    if (!opt) return;
    onPick?.(opt.raw);
    onHide?.();
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {err && <Alert variant="danger">{err}</Alert>}

        <div className="d-flex gap-2">
          <Form.Control
            size="sm"
            style={{ fontSize: "15px", height: "35px" }}
            autoFocus
            placeholder="Caută după serie, cod echipament, model, client..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
                fetchPage(1, q);
              }
            }}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            style={{
              fontSize: "15px",
              height: "35px",
              whiteSpace: "nowrap",
            }}
            disabled={busy}
            onClick={() => fetchPage(1, q)}
          >
            Refresh
          </Button>
        </div>

        <div className="mt-3">
          {busy && page === 1 && items.length === 0 ? (
            <div className="d-flex align-items-center gap-2">
              <Spinner size="sm" />
              <span>Se încarcă...</span>
            </div>
          ) : (
            <>
              <div
                style={{ maxHeight: "350px", overflowY: "auto" }}
                onScroll={handleScroll}
              >
                <Table hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Serie</th>
                      <th>Cod</th>
                      <th>Model/Piesă</th>
                      <th>Client (Utilizator)</th>
                      <th>Locație</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr
                        key={String(it.id)}
                        role="button"
                        onClick={() => pick(it)}
                        style={{ cursor: "pointer" }}
                      >
                        <td className="fw-bold">{it.seria}</td>
                        <td>{it.codech || "-"}</td>
                        <td>{it.part_name || "-"}</td>
                        <td>{it.client_name || "-"}</td>
                        <td>{it.raw?.bxlocatie || "-"}</td>
                      </tr>
                    ))}

                    {items.length === 0 && !busy && (
                      <tr>
                        <td colSpan={5} className="text-muted text-center py-3">
                          Nu s-a găsit niciun echipament matching.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {busy && page > 1 && (
                <div className="d-flex align-items-center gap-2 mt-2 justify-content-center">
                  <Spinner size="sm" />
                  <span className="text-muted" style={{ fontSize: 12 }}>
                    Se încarcă mai multe...
                  </span>
                </div>
              )}

              <div className="d-flex justify-content-between align-items-center mt-2">
                <div className="text-muted" style={{ fontSize: 12 }}>
                  {total != null ? `Total echipamente: ${total}` : ""}
                </div>
              </div>
            </>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Închide
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
